import React, { useState } from 'react';
import { Download, Upload, Settings as SettingsIcon } from 'lucide-react';
import { motion } from 'framer-motion';
import Layout from '../components/Layout';
import { getStoredData, saveData } from '../utils/storageUtils';
import { emojiSets, EmojiSet } from '../utils/moodUtils';

const SettingsView: React.FC = () => {
  const [eveningActivityType, setEveningActivityType] = useState(() => {
    return localStorage.getItem('eveningActivityType') || 'reflection';
  });
  
  const [reflectionPerPeriod, setReflectionPerPeriod] = useState(() => {
    return localStorage.getItem('reflectionPerPeriod') === 'true';
  });

  const [selectedEmojiSet, setSelectedEmojiSet] = useState(() => {
    return localStorage.getItem('selectedEmojiSet') || 'Classic';
  });

  const handleEveningActivityTypeChange = (type: 'reflection' | 'meditation') => {
    setEveningActivityType(type);
    localStorage.setItem('eveningActivityType', type);
  };

  const handleReflectionPerPeriodToggle = () => {
    const newValue = !reflectionPerPeriod;
    setReflectionPerPeriod(newValue);
    localStorage.setItem('reflectionPerPeriod', String(newValue));
  };

  const handleEmojiSetChange = (setName: string) => {
    setSelectedEmojiSet(setName);
    localStorage.setItem('selectedEmojiSet', setName);
  };

  const handleExportData = () => {
    const data = getStoredData();
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `niko-niko-backup-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleImportData = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const importedData = JSON.parse(e.target?.result as string);
        saveData(importedData);
        alert('Data imported successfully!');
        window.location.reload();
      } catch (error) {
        alert('Error importing data. Please check the file format.');
      }
    };
    reader.readAsText(file);
  };

  return (
    <Layout title="Settings">
      <div className="max-w-2xl mx-auto">
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="bg-purple-100 p-2 rounded-full">
              <SettingsIcon size={24} className="text-purple-600" />
            </div>
            <h2 className="text-xl font-semibold">Settings</h2>
          </div>

          <div className="space-y-6">
            <div>
              <h3 className="font-medium text-gray-900 mb-3">Evening Activity</h3>
              <p className="text-sm text-gray-500 mb-4">Choose your preferred evening activity</p>
              <div className="space-y-3">
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="eveningActivity"
                    value="reflection"
                    checked={eveningActivityType === 'reflection'}
                    onChange={() => handleEveningActivityTypeChange('reflection')}
                    className="text-purple-600 focus:ring-purple-500"
                  />
                  <span className="ml-2 text-gray-700">Evening Reflection</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="eveningActivity"
                    value="meditation"
                    checked={eveningActivityType === 'meditation'}
                    onChange={() => handleEveningActivityTypeChange('meditation')}
                    className="text-purple-600 focus:ring-purple-500"
                  />
                  <span className="ml-2 text-gray-700">Evening Guided Meditation</span>
                </label>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-medium text-gray-900">Reflection Per Time Period</h3>
                <p className="text-sm text-gray-500">Allow adding notes for each time period (morning, afternoon, evening)</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  className="sr-only peer"
                  checked={reflectionPerPeriod}
                  onChange={handleReflectionPerPeriodToggle}
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
              </label>
            </div>

            <div className="border-t border-gray-100 pt-6">
              <h3 className="font-medium text-gray-900 mb-4">Emoji Style</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {emojiSets.map((set: EmojiSet) => (
                  <motion.button
                    key={set.name}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => handleEmojiSetChange(set.name)}
                    className={`p-4 rounded-lg border ${
                      selectedEmojiSet === set.name
                        ? 'border-purple-500 bg-purple-50'
                        : 'border-gray-200 hover:border-purple-200'
                    }`}
                  >
                    <div className="text-2xl mb-2">
                      {Object.values(set.emojis).join(' ')}
                    </div>
                    <div className="text-sm font-medium text-gray-900">{set.name}</div>
                  </motion.button>
                ))}
              </div>
            </div>

            <div className="border-t border-gray-100 pt-6">
              <h3 className="font-medium text-gray-900 mb-4">Data Management</h3>
              
              <div className="space-y-4">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={handleExportData}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                >
                  <Download size={20} />
                  Export Data Backup
                </motion.button>

                <label className="block">
                  <motion.div
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2 border-2 border-dashed border-purple-300 text-purple-600 rounded-lg hover:bg-purple-50 transition-colors cursor-pointer"
                  >
                    <Upload size={20} />
                    Import Data Backup
                    <input
                      type="file"
                      accept=".json"
                      onChange={handleImportData}
                      className="hidden"
                    />
                  </motion.div>
                </label>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default SettingsView;